import { useTranslation } from 'react-i18next';
/**
 * Manage Links Dialog (Insert tab)
 *
 * BIM-style hub listing every external reference grouped by type. Reads
 * the shared LinkManager registry (subscribed, so it live-updates when a
 * tool adds a link) and drives the wrapper element through the stores:
 * visibility via hide/unhide, reload via the CAD parser or registry, unload
 * via status + hidden element, remove by deleting both records.
 */

import { useEffect, useMemo, useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Button,
    Box,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Checkbox,
} from '@chakra-ui/react';
import { RefreshCw, Trash2, Unplug } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { ManageDialogFrame, ManageDialogCloseFooter } from '../manage/ManageDialogFrame';
import { sharedLinkManager, LinkInstance, LinkType } from '../../core/LinkManager';
import { CadLinkStore } from '../../services/cad/CadLinkStore';
import { readCadFile, applyParsedGeometryToElement } from '../../tools/LinkCADTool';

/** Registry tabs mirroring BIM's Manage Links window. */
const LINK_TABS: { type: LinkType; label: string }[] = [
    { type: 'BIM', label: 'BIM' },
    { type: 'CAD', label: 'CAD' },
    { type: 'IFC', label: 'IFC' },
    { type: 'PDF', label: 'PDF' },
    { type: 'Image', label: 'Images' },
    { type: 'PointCloud', label: 'Point Cloud' },
    { type: 'Topography', label: 'Topography' },
    { type: 'Coordination', label: 'Coordination' },
];

/** Notify the global toast host. */
function toast(message: string, status: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    window.dispatchEvent(new CustomEvent('yap:notify', { detail: { message, status } }));
}

function formatSize(bytes: number): string {
    if (!bytes || bytes <= 0) return '--';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ts: number): string {
    if (!ts) return '--';
    return new Date(ts).toLocaleString();
}

export function ManageLinksDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'manageLinks');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const hideElementsInView = useAppStore((state) => state.hideElementsInView);
    const unhideElementsInView = useAppStore((state) => state.unhideElementsInView);

    // Registry snapshot: re-read whenever the manager emits.
    const [version, setVersion] = useState(0);
    useEffect(() => {
        if (!isOpen) return;
        return sharedLinkManager.subscribe(() => setVersion(v => v + 1));
    }, [isOpen]);

    const links = useMemo(() => sharedLinkManager.getAllLinks(), [version, isOpen]);

    const wrapperFor = (link: LinkInstance) =>
        link.documentId ? useBIMStore.getState().project.elements.get(link.documentId) : undefined;

    const toggleVisibility = (link: LinkInstance, visible: boolean) => {
        sharedLinkManager.setVisibility(link.id, visible);
        const docId = link.documentId;
        if (!docId) return;
        if (visible) {
            unhideElementsInView([docId]);
            if (link.status === 'Unloaded') sharedLinkManager.setStatus(link.id, 'Loaded');
        } else {
            hideElementsInView([docId]);
        }
    };

    const reload = async (link: LinkInstance) => {
        const docId = link.documentId;
        // CAD references re-parse their remembered source file.
        if (link.type === 'CAD' && docId) {
            let file = CadLinkStore.getSource(docId)?.file ?? null;
            if (!file) {
                toast(`Session source for "${link.name}" was lost. Use the element context menu Reload to re-pick it.`, 'warning');
                return;
            }
            try {
                const result = await readCadFile(file);
                applyParsedGeometryToElement(docId, file, result, useBIMStore.getState());
                CadLinkStore.markLoaded(docId);
                sharedLinkManager.reloadLink(link.id);
                toast(`Reloaded "${link.name}" — ${result.lines.length} lines.`, 'success');
                return;
            } catch (err) {
                toast(`Reload failed: ${(err as Error).message}`, 'error');
                return;
            }
        }
        sharedLinkManager.reloadLink(link.id);
        if (docId) unhideElementsInView([docId]);
        toast(`Reloaded "${link.name}".`, 'success');
    };

    const unload = (link: LinkInstance) => {
        sharedLinkManager.unloadLink(link.id);
        if (link.documentId) hideElementsInView([link.documentId]);
        toast(`Unloaded "${link.name}".`, 'info');
    };

    const remove = (link: LinkInstance) => {
        const wrapper = wrapperFor(link);
        if (wrapper) useBIMStore.getState().removeElement(wrapper.id);
        sharedLinkManager.removeLink(link.id);
        toast(`Removed "${link.name}".`, 'info');
    };

    const renderTable = (tabLinks: LinkInstance[]) => (
        <Box>
            {tabLinks.length === 0 ? (
                <Text fontSize="sm" color="var(--color-text-secondary)" py={4}>{t('insert.noReferencesOfThisTypeAreLinkedUseTheInsertTabLinkToolsToAddOne')}</Text>
            ) : (
                <Table size="sm" variant="simple">
                    <Thead>
                        <Tr>
                            <Th fontSize="xs">{t('insert.name')}</Th>
                            <Th fontSize="xs">{t('insert.source')}</Th>
                            <Th fontSize="xs">{t('insert.status')}</Th>
                            <Th fontSize="xs">{t('insert.size')}</Th>
                            <Th fontSize="xs">{t('insert.lastLoaded')}</Th>
                            <Th fontSize="xs">{t('insert.visible')}</Th>
                            <Th fontSize="xs">{t('insert.actions')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {tabLinks.map((link) => (
                            <Tr key={link.id}>
                                <Td fontSize="xs" maxW="180px"><Text isTruncated>{link.name}</Text></Td>
                                <Td fontSize="xs" maxW="220px" color="var(--color-text-secondary)"><Text isTruncated title={link.path}>{link.path}</Text></Td>
                                <Td fontSize="xs">
                                    <Text
                                        color={link.status === 'Loaded' ? 'var(--color-status-success, #2e9e4f)'
                                            : link.status === 'NotFound' || link.status === 'Outdated' ? 'var(--color-status-error, #d64545)'
                                                : 'var(--color-text-secondary)'}
                                    >
                                        {link.status}
                                    </Text>
                                </Td>
                                <Td fontSize="xs">{formatSize(link.size)}</Td>
                                <Td fontSize="xs">{formatTime(link.lastModified)}</Td>
                                <Td>
                                    <Checkbox
                                        size="sm"
                                        colorScheme="green"
                                        isChecked={link.visible && link.status !== 'Unloaded'}
                                        onChange={(e) => toggleVisibility(link, e.target.checked)}
                                    />
                                </Td>
                                <Td>
                                    <HStack spacing={1}>
                                        <Button size="xs" variant="ghost" leftIcon={<RefreshCw size={12} />} onClick={() => void reload(link)} title={t('insert.reload')}>{t('insert.reload')}</Button>
                                        <Button size="xs" variant="ghost" leftIcon={<Unplug size={12} />} onClick={() => unload(link)} title={t('insert.unload')}>{t('insert.unload')}</Button>
                                        <Button size="xs" variant="ghost" color="var(--color-status-error, #d64545)" leftIcon={<Trash2 size={12} />} onClick={() => remove(link)} title={t('insert.removeLink')}>{t('insert.remove')}</Button>
                                    </HStack>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            )}
        </Box>); return (<ManageDialogFrame
            title={t('insert.manageLinks')}
            isOpen={isOpen}
            onClose={onClose}
            size="4xl"
            footer={<ManageDialogCloseFooter onClose={onClose} />}
        >
            <VStack spacing={3} align="stretch">
                <Tabs size="sm" isFitted variant="enclosed" colorScheme="blue">
                    <TabList flexWrap="nowrap" overflowX="auto">
                        {LINK_TABS.map((tab) => (
                            <Tab key={tab.type} fontSize="xs" whiteSpace="nowrap" flexShrink={0}>
                                {tab.label}
                                {links.filter(l => l.type === tab.type).length > 0 && (
                                    <Text as="span" ml={1} color="var(--color-text-secondary)">
                                        ({links.filter(l => l.type === tab.type).length})
                                    </Text>
                                )}
                            </Tab>
                        ))}
                    </TabList>
                    <TabPanels>
                        {LINK_TABS.map((tab) => (
                            <TabPanel key={tab.type} p={3}>
                                {renderTable(links.filter(l => l.type === tab.type))}
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Tabs>
            </VStack>
        </ManageDialogFrame>
    );
}
